import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiUsers, FiCalendar, FiHome, FiMenu, FiX, FiUserCheck, FiZap, FiLogOut } from 'react-icons/fi';
import { useUsers, ROLES } from '../context/UsersContext';

const Layout = ({ children, onLogout }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { currentUser } = useUsers();

  const allNavItems = [
    { path: '/',         label: 'Panel',     icon: FiHome,      roles: ['superadmin', 'admin', 'sales'] },
    { path: '/leads',    label: 'Leads',     icon: FiUsers,     roles: ['superadmin', 'admin', 'sales'] },
    { path: '/calendar', label: 'Calendario', icon: FiCalendar,  roles: ['superadmin', 'admin', 'sales'] },
    { path: '/users',    label: 'Usuarios',  icon: FiUserCheck, roles: ['superadmin', 'admin'] },
  ];

  const navItems = allNavItems.filter(item => item.roles.includes(currentUser?.role));

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: '#F8FAFC' }}>
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-20 bg-black/50 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 transform transition-transform duration-300 lg:static lg:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ backgroundColor: '#0F172A', borderRight: '1px solid #1E293B' }}>

        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-5" style={{ borderBottom: '1px solid #1E293B' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #2563EB, #38BDF8)' }}>
              <SafeIcon icon={FiZap} className="text-white text-sm" />
            </div>
            <div className="leading-tight">
              <span className="text-white font-black text-sm tracking-tight block">Fullpower</span>
              <span className="font-bold text-xs tracking-widest uppercase" style={{ color: '#38BDF8' }}>LeadFlow</span>
            </div>
          </div>
          <button className="lg:hidden" style={{ color: '#94A3B8' }} onClick={() => setIsMobileMenuOpen(false)}>
            <SafeIcon icon={FiX} className="text-2xl" />
          </button>
        </div>

        {/* Nav */}
        <nav className="p-3 space-y-0.5 mt-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path} to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-all text-sm"
                style={isActive ? { backgroundColor: '#2563EB', color: '#FFFFFF' } : { color: '#94A3B8' }}
                onMouseEnter={e => { if (!isActive) { e.currentTarget.style.backgroundColor = '#1E293B'; e.currentTarget.style.color = '#E2E8F0'; } }}
                onMouseLeave={e => { if (!isActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#94A3B8'; } }}>
                <SafeIcon icon={item.icon} className="text-base flex-shrink-0" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User area + Logout */}
        {currentUser && (
          <div className="absolute bottom-0 left-0 right-0 px-4 py-4" style={{ borderTop: '1px solid #1E293B' }}>
            <div className="flex items-center gap-3 px-2 mb-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #2563EB, #14B8A6)' }}>
                {currentUser.name.charAt(0)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white truncate">{currentUser.name}</p>
                <p className="text-[10px] font-bold tracking-wide uppercase" style={{ color: '#38BDF8' }}>
                  {ROLES[currentUser.role]?.label || currentUser.role}
                </p>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{ color: '#64748B', backgroundColor: 'transparent' }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#1E293B'; e.currentTarget.style.color = '#EF4444'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#64748B'; }}>
              <SafeIcon icon={FiLogOut} className="text-base flex-shrink-0" />
              Cerrar sesión
            </button>
          </div>
        )}
      </aside>

      <main className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Mobile header */}
        <header className="flex items-center justify-between h-16 px-4 bg-white border-b lg:hidden" style={{ borderColor: '#E2E8F0' }}>
          <button onClick={() => setIsMobileMenuOpen(true)} style={{ color: '#64748B' }}>
            <SafeIcon icon={FiMenu} className="text-2xl" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2563EB, #38BDF8)' }}>
              <SafeIcon icon={FiZap} className="text-white text-xs" />
            </div>
            <span className="text-sm font-black" style={{ color: '#0F172A' }}>Fullpower <span style={{ color: '#2563EB' }}>LeadFlow</span></span>
          </div>
          <button onClick={onLogout} title="Cerrar sesión" style={{ color: '#94A3B8' }}
            onMouseEnter={e => e.currentTarget.style.color = '#EF4444'}
            onMouseLeave={e => e.currentTarget.style.color = '#94A3B8'}>
            <SafeIcon icon={FiLogOut} className="text-lg" />
          </button>
        </header>

        <div className="flex-1 overflow-auto p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;